import { useState } from 'react'
import { Plane, Plus, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { lawyerCasesAPI } from '@/utils/api'
import { EditableSegmentCard } from './EditableSegmentCard'
import type { FlightSegment } from './types'

interface FlightSegmentsCardProps {
  caseId: number
  segments: FlightSegment[]
  onUpdate: () => void
}

export function FlightSegmentsCard({ caseId, segments, onUpdate }: FlightSegmentsCardProps) {
  // Editing state
  const [editingSegmentId, setEditingSegmentId] = useState<number | null>(null)
  const [editSegmentData, setEditSegmentData] = useState<Partial<FlightSegment>>({})
  const [savingSegmentId, setSavingSegmentId] = useState<number | null>(null)
  const [deletingSegmentId, setDeletingSegmentId] = useState<number | null>(null)

  // Add state
  const [showAddSegment, setShowAddSegment] = useState(false)
  const [newSegment, setNewSegment] = useState({
    segment_type: 'original' as 'original' | 'actual',
    carrier: '',
    flight_no: '',
    dep_iata: '',
    arr_iata: '',
    dep_dt: '',
    arr_dt: '',
    booking_class: '',
  })
  const [addingSegment, setAddingSegment] = useState(false)

  const originalSegments = segments.filter((s) => s.segment_type === 'original')
  const actualSegments = segments.filter((s) => s.segment_type === 'actual')

  const startEditSegment = (segment: FlightSegment) => {
    setEditingSegmentId(segment.id)
    setEditSegmentData({
      segment_type: segment.segment_type,
      carrier: segment.carrier,
      flight_no: segment.flight_no,
      dep_iata: segment.dep_iata,
      arr_iata: segment.arr_iata,
      dep_dt: segment.dep_dt ? segment.dep_dt.slice(0, 16) : '',
      arr_dt: segment.arr_dt ? segment.arr_dt.slice(0, 16) : '',
      booking_class: segment.booking_class || '',
    })
  }

  const cancelEditSegment = () => {
    setEditingSegmentId(null)
    setEditSegmentData({})
  }

  const saveSegment = async (segmentId: number) => {
    setSavingSegmentId(segmentId)
    try {
      await lawyerCasesAPI.updateSegment(caseId, segmentId, editSegmentData)
      toast.success('Flugsegment aktualisiert')
      setEditingSegmentId(null)
      setEditSegmentData({})
      onUpdate()
    } catch (err) {
      console.error('Failed to update segment:', err)
      toast.error('Fehler beim Speichern')
    } finally {
      setSavingSegmentId(null)
    }
  }

  const deleteSegment = async (segmentId: number) => {
    setDeletingSegmentId(segmentId)
    try {
      await lawyerCasesAPI.deleteSegment(caseId, segmentId)
      toast.success('Flugsegment gelöscht')
      onUpdate()
    } catch (err) {
      console.error('Failed to delete segment:', err)
      toast.error('Fehler beim Löschen')
    } finally {
      setDeletingSegmentId(null)
    }
  }

  const addSegment = async () => {
    if (!newSegment.carrier || !newSegment.flight_no || !newSegment.dep_iata || !newSegment.arr_iata) {
      toast.error('Flugnummer und Flughäfen erforderlich')
      return
    }
    setAddingSegment(true)
    try {
      await lawyerCasesAPI.createSegment(caseId, newSegment)
      toast.success('Flugsegment hinzugefügt')
      setShowAddSegment(false)
      setNewSegment({
        segment_type: 'original',
        carrier: '',
        flight_no: '',
        dep_iata: '',
        arr_iata: '',
        dep_dt: '',
        arr_dt: '',
        booking_class: '',
      })
      onUpdate()
    } catch (err) {
      console.error('Failed to add segment:', err)
      toast.error('Fehler beim Hinzufügen')
    } finally {
      setAddingSegment(false)
    }
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Plane className="h-5 w-5 text-slate-400" />
          Flugsegmente
          <Badge variant="secondary">{segments.length}</Badge>
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAddSegment(true)}
          className="gap-1"
        >
          <Plus className="h-3.5 w-3.5" />
          Hinzufügen
        </Button>
      </div>

      {/* Add Segment Form */}
      {showAddSegment && (
        <div className="mb-4 p-3 bg-highlight-hover rounded-lg border border-highlight">
          <div className="grid grid-cols-4 gap-2 mb-2">
            <Select
              value={newSegment.segment_type}
              onValueChange={(v) => setNewSegment({ ...newSegment, segment_type: v as 'original' | 'actual' })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Typ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="original">Original</SelectItem>
                <SelectItem value="actual">Tatsächlich</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Carrier (LH)"
              value={newSegment.carrier}
              onChange={(e) => setNewSegment({ ...newSegment, carrier: e.target.value.toUpperCase() })}
              maxLength={2}
              className="font-mono uppercase"
            />
            <Input
              placeholder="Flugnr."
              value={newSegment.flight_no}
              onChange={(e) => setNewSegment({ ...newSegment, flight_no: e.target.value })}
              className="font-mono"
            />
            <Input
              placeholder="Klasse"
              value={newSegment.booking_class}
              onChange={(e) => setNewSegment({ ...newSegment, booking_class: e.target.value.toUpperCase() })}
              maxLength={2}
              className="font-mono uppercase"
            />
          </div>
          <div className="grid grid-cols-4 gap-2 mb-2">
            <Input
              placeholder="DEP (FRA)"
              value={newSegment.dep_iata}
              onChange={(e) => setNewSegment({ ...newSegment, dep_iata: e.target.value.toUpperCase() })}
              maxLength={3}
              className="font-mono uppercase"
            />
            <Input
              placeholder="ARR (JFK)"
              value={newSegment.arr_iata}
              onChange={(e) => setNewSegment({ ...newSegment, arr_iata: e.target.value.toUpperCase() })}
              maxLength={3}
              className="font-mono uppercase"
            />
            <Input
              type="datetime-local"
              placeholder="Abflug"
              value={newSegment.dep_dt}
              onChange={(e) => setNewSegment({ ...newSegment, dep_dt: e.target.value })}
            />
            <Input
              type="datetime-local"
              placeholder="Ankunft"
              value={newSegment.arr_dt}
              onChange={(e) => setNewSegment({ ...newSegment, arr_dt: e.target.value })}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowAddSegment(false)
                setNewSegment({
                  segment_type: 'original',
                  carrier: '',
                  flight_no: '',
                  dep_iata: '',
                  arr_iata: '',
                  dep_dt: '',
                  arr_dt: '',
                  booking_class: '',
                })
              }}
            >
              Abbrechen
            </Button>
            <Button size="sm" onClick={addSegment} disabled={addingSegment}>
              {addingSegment ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}
            </Button>
          </div>
        </div>
      )}

      {/* Original Booking */}
      {originalSegments.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-medium text-slate-600 mb-2">Ursprüngliche Buchung</h3>
          <div className="space-y-2">
            {originalSegments.map((segment) => (
              <EditableSegmentCard
                key={segment.id}
                segment={segment}
                isEditing={editingSegmentId === segment.id}
                editData={editSegmentData}
                setEditData={setEditSegmentData}
                onStartEdit={() => startEditSegment(segment)}
                onCancelEdit={cancelEditSegment}
                onSave={() => saveSegment(segment.id)}
                onDelete={() => deleteSegment(segment.id)}
                isSaving={savingSegmentId === segment.id}
                isDeleting={deletingSegmentId === segment.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Actual Routing */}
      {actualSegments.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-600 mb-2">Tatsächliche Route</h3>
          <div className="space-y-2">
            {actualSegments.map((segment) => (
              <EditableSegmentCard
                key={segment.id}
                segment={segment}
                isEditing={editingSegmentId === segment.id}
                editData={editSegmentData}
                setEditData={setEditSegmentData}
                onStartEdit={() => startEditSegment(segment)}
                onCancelEdit={cancelEditSegment}
                onSave={() => saveSegment(segment.id)}
                onDelete={() => deleteSegment(segment.id)}
                isSaving={savingSegmentId === segment.id}
                isDeleting={deletingSegmentId === segment.id}
              />
            ))}
          </div>
        </div>
      )}

      {segments.length === 0 && (
        <p className="text-slate-500 text-sm">Noch keine Flugsegmente hinzugefügt.</p>
      )}
    </div>
  )
}
